import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { normalizeQuillHtml } from "../../services/common/normalizeQuillHtml";
import releaseDetailsService from "../../app/data/release-details/releaseDetailsService";
import { ReleaseDetailsAttachmentModel } from "../../app/data/release-details/models";
import ReleaseDetailsEditor from "./releaseDetailsEditor";

const FILE_MAX_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 10;
const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "png", "jpg", "jpeg"];

interface ReleaseDetailsSectionProps {
  announcementId?: string;
  enabled: boolean;
  onDirtyChange: (dirty: boolean) => void;
  registerSave: (save: (announcementId: string) => Promise<void>) => void;
  registerReset: (reset: () => void) => void;
}

const isBlankHtml = (html: string): boolean => {
  if (/<img\b/i.test(html || "")) {
    return false;
  }
  const text = (html || "")
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, "")
    .replace(/\u00a0/g, "")
    .trim();
  return text.length === 0;
};

const extensionOf = (fileName: string): string => {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

const ReleaseDetailsSection: React.FC<ReleaseDetailsSectionProps> = ({
  announcementId,
  enabled,
  onDirtyChange,
  registerSave,
  registerReset
}) => {
  const [body, setBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [attachments, setAttachments] = useState<ReleaseDetailsAttachmentModel[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const bodyRef = useRef("");
  const pendingRef = useRef<File[]>([]);
  const loadToken = useRef(0);

  const rememberBody = (value: string) => {
    bodyRef.current = value;
    setBody(value);
  };

  const rememberPending = (files: File[]) => {
    pendingRef.current = files;
    setPendingFiles(files);
  };

  const load = useCallback((nextAnnouncementId?: string) => {
    const token = ++loadToken.current;
    if (!nextAnnouncementId) {
      if (!isBlankHtml(bodyRef.current) || pendingRef.current.length > 0) {
        return;
      }
      rememberBody("");
      setSavedBody("");
      setAttachments([]);
      rememberPending([]);
      return;
    }
    if (!isBlankHtml(bodyRef.current) || pendingRef.current.length > 0) {
      return;
    }
    releaseDetailsService.getForAnnouncement(nextAnnouncementId).then((details) => {
      if (loadToken.current !== token) {
        return;
      }
      const nextBody = details?.body || "";
      rememberBody(nextBody);
      setSavedBody(nextBody);
      setAttachments(details?.attachments || []);
      rememberPending([]);
    }).catch((error: Error) => {
      if (loadToken.current !== token) {
        return;
      }
      toast.error(error.message || "Unable to load release details");
    });
  }, []);

  useEffect(() => {
    load(announcementId);
  }, [load, announcementId]);

  useEffect(() => {
    const normalizedBody = isBlankHtml(body) ? "" : body;
    const normalizedSavedBody = isBlankHtml(savedBody) ? "" : savedBody;
    onDirtyChange(normalizedBody !== normalizedSavedBody || pendingFiles.length > 0);
  }, [body, savedBody, pendingFiles, onDirtyChange]);

  useEffect(() => {
    registerReset(() => load(announcementId));
  }, [load, announcementId, registerReset]);

  useEffect(() => {
    registerSave(async (id: string) => {
      loadToken.current += 1;
      const currentBody = bodyRef.current;
      const files = pendingRef.current.slice();
      const saved = await releaseDetailsService.save(id, currentBody);
      let nextAttachments = saved?.attachments || [];
      if (isBlankHtml(currentBody) && files.length === 0) {
        nextAttachments = [];
      }
      for (const file of files) {
        const uploaded = await releaseDetailsService.uploadAttachment(id, file);
        nextAttachments = [...nextAttachments, uploaded];
      }
      const nextBody = saved?.body || (isBlankHtml(currentBody) ? "" : currentBody);
      rememberBody(nextBody);
      setSavedBody(nextBody);
      setAttachments(nextAttachments);
      rememberPending([]);
    });
  }, [registerSave]);

  const onPickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || !enabled) {
      return;
    }
    if (!ALLOWED_EXTENSIONS.includes(extensionOf(file.name))) {
      toast.error("Allowed file types are PDF, DOC, DOCX, PNG, JPG, and JPEG.");
      return;
    }
    if (file.size > FILE_MAX_SIZE) {
      toast.error("Each file must be 10 MB or smaller.");
      return;
    }
    if (attachments.length + pendingFiles.length >= MAX_FILES) {
      toast.error("A release can include at most 10 files.");
      return;
    }
    if (!announcementId) {
      rememberPending([...pendingFiles, file]);
      return;
    }
    setUploading(true);
    releaseDetailsService.uploadAttachment(announcementId, file).then((uploaded) => {
      setAttachments((current) => [...current, uploaded]);
    }).catch((error: Error) => {
      toast.error(error.message || "Unable to upload the file");
    }).finally(() => {
      setUploading(false);
    });
  };

  const removePending = (index: number) => {
    rememberPending(pendingFiles.filter((_, fileIndex) => fileIndex !== index));
  };

  const removeAttachment = (attachment: ReleaseDetailsAttachmentModel) => {
    releaseDetailsService.deleteAttachment(attachment.id).then(() => {
      setAttachments((current) => current.filter((item) => item.id !== attachment.id));
    }).catch((error: Error) => {
      toast.error(error.message || "Unable to remove the file");
    });
  };

  return (
    <div className="xgs-announcement__form__release">
      <div className="xgs-announcement__form__release__title">Release details</div>
      <div className="xgs-announcement__form__notes">
        Optional instructions and user guides. They are saved with the announcement and stay available after it is turned off.
      </div>
      <div className="xgs-release-details-editor">
        {enabled && (
          <ReleaseDetailsEditor
            value={body}
            onChange={rememberBody}
          />
        )}
        {!enabled && (
          <div
            className="ql-editor"
            dangerouslySetInnerHTML={{ __html: normalizeQuillHtml(body) }}
          />
        )}
      </div>
      <div className="xgs-announcement__form__release__files">
        {attachments.map((attachment) => (
          <div className="xgs-announcement__form__release__file" key={attachment.id}>
            <span>{attachment.fileName}</span>
            {enabled && (
              <button type="button" onClick={() => removeAttachment(attachment)}>Remove</button>
            )}
          </div>
        ))}
        {pendingFiles.map((file, index) => (
          <div className="xgs-announcement__form__release__file" key={`${file.name}-${index}`}>
            <span>{file.name}</span>
            {enabled && (
              <button type="button" onClick={() => removePending(index)}>Remove</button>
            )}
          </div>
        ))}
      </div>
      {enabled && (
        <div className="xgs-announcement__form__release__upload">
          <label className="xgs-announcement__form__release__upload-label">
            {uploading ? "Uploading..." : "Add file"}
            <input
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={onPickFile}
              disabled={uploading}
            />
          </label>
          {!announcementId && (
            <span className="xgs-announcement__form__notes">Files are uploaded when you publish.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default ReleaseDetailsSection;
